import {
  HttpClient,
  HttpContext,
  HttpEventType,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  GenericHttpGetRequest,
  GenericHttpPostRequest,
  GenericHttpRequest,
} from '@shared/models';
import { ReqType } from '@shared/utils';
import { routeMapping } from '@shared/utils/route-mapping';
import { Observable, filter, map } from 'rxjs';

@Injectable()
export class GenericHttpClientService {
  constructor(protected httpClient: HttpClient) {}

  get<TModel>(requestContext: GenericHttpGetRequest): Observable<TModel> {
    const request = this.buildRequest(requestContext, 'GET');
    return this.getHttpResponse<TModel>(
      request,
      requestContext.observeResponse
    );
  }

  post<TModel, TDto = unknown>(
    requestContext: GenericHttpPostRequest<TDto>
  ): Observable<TModel> {
    const request = this.buildRequest(requestContext, 'POST');

    return this.getHttpResponse<TModel>(request);
  }

  private getHttpResponse<T>(
    request: HttpRequest<unknown>,
    observeResponse: boolean = false
  ): Observable<T> {
    return this.httpClient.request(request).pipe(
      filter((x) => x.type === HttpEventType.Response),
      map((x) =>
        observeResponse
          ? (x as HttpResponse<unknown> as T)
          : (x as HttpResponse<T>).body
      )
    );
  }

  private buildRequest<T = unknown>(
    requestContext: GenericHttpRequest<T>,
    type: 'GET' | 'POST'
  ): HttpRequest<T> {
    const { context, headers, params, args, responseType } = requestContext;

    const requestOptions = {
      context: new HttpContext().set(ReqType, context),
      headers,
      params,
      responseType,
    };

    switch (type) {
      case 'POST':
        const { body } = requestContext as GenericHttpPostRequest<T>;
        return new HttpRequest<T>(
          'POST',
          `${routeMapping[context](args)}`,
          body,
          requestOptions
        );
      case 'GET':
        return new HttpRequest<T>(
          'GET',
          `${routeMapping[context](args)}`,
          requestOptions
        );
      default:
        //this is here for safety to ensure that all the values in type are covered in switch
        //if one is missing this will generate a compile error
        const _exhaustiveCheck: never = type;
        return _exhaustiveCheck;
    }
  }
}
